import { useRef, useState, useEffect } from "react";
import { getLocalStorageItem } from "../utils/localStorage";

const useSignaturePad = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const { firstName, lastName } = getLocalStorageItem("userDetails") || {};
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000"; // Black stroke color

    const startDrawing = (event: MouseEvent | TouchEvent) => {
      setIsDrawing(true);
      const { offsetX, offsetY } = getEventCoordinates(event);
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
    };

    const draw = (event: MouseEvent | TouchEvent) => {
      if (!isDrawing) return;
      hasDrawnRef.current = true;
      const { offsetX, offsetY } = getEventCoordinates(event);
      ctx.lineTo(offsetX, offsetY);
      ctx.stroke();
    };

    const stopDrawing = () => {
      if (!isDrawing || !hasDrawnRef.current) return;
      setIsDrawing(false);
      hasDrawnRef.current = false;
      ctx.beginPath();
      saveSignatureAsFile();
    };

    const getEventCoordinates = (event: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      let offsetX: number, offsetY: number;

      if ("touches" in event) {
        offsetX = event.touches[0].clientX - rect.left;
        offsetY = event.touches[0].clientY - rect.top;
      } else {
        offsetX = event.clientX - rect.left;
        offsetY = event.clientY - rect.top;
      }

      return { offsetX, offsetY };
    };

    // Event listeners
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("mouseleave", stopDrawing);

    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("touchmove", draw);
    canvas.addEventListener("touchend", stopDrawing);

    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("mouseleave", stopDrawing);

      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
    };
  }, [isDrawing]);

  const saveSignatureAsFile = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File(
          [blob],
          `${firstName} ${lastName} signature.png`,
          { type: "image/png" }
        );
        setSignatureFile(file);
      }
    }, "image/png");
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureFile(null);
  };

  return { canvasRef, clearCanvas, signatureFile };
};

export default useSignaturePad;
